import { MakeRequired } from '../../../../models/basic-objects';
import { MappedProfileType } from '../../../../components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import { GetBitlyAccountsQuery } from '../../../../models/graphql';
import { countSymbols } from '../../../../helpers/countSymbols';
import { Text } from 'slate';
import { CustomParagraph, CustomSlateElement, CustomText } from '../../../../models/slate-editor';
import { CardinalSocialProfileType } from '../../helpers/mapProfileFragmentToSelectedSocialProfileValue';
import { PostEditorLink } from '../../../../models/post-editor';
import { mapUtmPatterns } from '../../helpers/mapUtmPatterns';
import { typeGuards } from '../helpers/typeGuards';

type CharLimitsObjectKeys = MakeRequired<MappedProfileType, '__typename'>['__typename'];
type CharLimitsObjectKeysWithValues = Exclude<CharLimitsObjectKeys, 'Bitly' | 'SocialProfile'>;

export type CharLimitsObject =
  | {
      [key in CharLimitsObjectKeysWithValues]: number;
    } & {
      [key in 'Bitly' | 'SocialProfile']: null;
    };

export const CharLimitsPerNetwork: CharLimitsObject = {
  Bitly: null,
  FacebookSocialProfile: 63206,
  GenericSocialProfile: 63206,
  GoogleSocialProfile: 1500,
  InstagramSocialProfile: 2200,
  LinkedInSocialProfile: 3000,
  PinterestSocialProfile: 500,
  SocialProfile: null,
  TwitterSocialProfile: 280,
  TikTokSocialProfile: 2200
};

export const FirstCommentCharLimitsPerNetwork = {
  FacebookSocialProfile: 2200,
  InstagramSocialProfile: 2200,
  LinkedInSocialProfile: 2000,
  Bitly: null,
  GoogleSocialProfile: null,
  PinterestSocialProfile: null,
  GenericSocialProfile: null,
  SocialProfile: null,
  TwitterSocialProfile: null,
  TikTokSocialProfile: null
};

export const BitlyLinkValueLength = 8; // The lenght of bitly after the domain including '/'. e.g. https://bit.ly/3K2BFVP

export const getBitlySettingsPerProfile = (
  profileData: GetBitlyAccountsQuery['workspace']['socialProfiles'][number],
  defaultBitly: GetBitlyAccountsQuery['workspace']['settings']['defaultBitly']
): string => (profileData.customBitly ? profileData.customBitly.domain ?? '' : defaultBitly?.domain || '');

type ReplacementFn = (substring: string, ...args: string[]) => string;
type ReplacementPattern = [string, string] | [RegExp, string] | [RegExp, ReplacementFn];

const mreplace = (replacements: ReplacementPattern[], original: string): string => {
  let result = original;
  for (const tuple of replacements) {
    const [x, y] = tuple;
    if (x instanceof RegExp && typeof y === 'function') {
      // TypeScript now knows y is a ReplacementFn
      result = result.replace(x, y);
    } else if (typeof y === 'string') {
      // TypeScript now knows y is a string
      result = result.replace(x, y);
    }
  }
  return result;
};

export const calculateLinkLengthPerProfile = (
  link: PostEditorLink,
  socialProfile: GetBitlyAccountsQuery['workspace']['socialProfiles'][number],
  defaultBitly: GetBitlyAccountsQuery['workspace']['settings']['defaultBitly'],
  categoryId: string | null
): number => {
  const { ACCOUNT, CATEGORY, NETWORK } = mapUtmPatterns(socialProfile, categoryId);
  const bitly = getBitlySettingsPerProfile(socialProfile, defaultBitly);
  switch (socialProfile.__typename) {
    case 'TwitterSocialProfile':
      return 23; // twitter links are always 23 chars long no metter what;
    case 'PinterestSocialProfile':
      const expandedLink = link.linkMetadata?.expandedUrl;
      return expandedLink ? countSymbols(expandedLink) : 0;
    case 'FacebookSocialProfile':
    case 'TikTokSocialProfile':
    case 'InstagramSocialProfile':
    case 'LinkedInSocialProfile':
    case 'GoogleSocialProfile':
    case 'SocialProfile': {
      return link.useBitly
        ? countSymbols(`https://${bitly}`) + BitlyLinkValueLength
        : link.url
        ? countSymbols(
            mreplace(
              [
                [/_?ACCOUNT_?/, ACCOUNT],
                [/_?CATEGORY_?/, CATEGORY],
                [/_?NETWORK?/, NETWORK]
              ],
              link.url
            )
          )
        : 0;
    }
    case undefined:
    default:
      return 0;
  }
};

export const getCharacterLimit = (
  socialProfiles: CardinalSocialProfileType[],
  isFirstComment?: boolean
): number | null => {
  return socialProfiles.reduce<number | null>((prev, current) => {
    if (prev !== null) {
      const charCountValuePerNetwork = current.__typename ? CharLimitsPerNetwork[current.__typename] : null;
      if (charCountValuePerNetwork === null) return prev;
      return Math.min(prev, charCountValuePerNetwork);
    }
    if (isFirstComment) {
      return current.__typename ? FirstCommentCharLimitsPerNetwork[current.__typename] : null;
    }
    return current.__typename ? CharLimitsPerNetwork[current.__typename] : null;
  }, null);
};

const serialize = (text: Array<CustomSlateElement | CustomText>): string => {
  return text
    .map(p => {
      if (Text.isText(p)) {
        return p.text;
      } else if (p.type === 'mention') {
        const { method } = p;
        return method === 'TWITTER_USER' ? `@${p.username}` : `@[${p.externalId}]`;
      } else if (p.type === 'link') {
        return '';
      } else {
        if (!typeGuards.isCustomSlateElementArray(p.children)) {
          throw new Error('paragraph children are not of type CustomSlateElement.');
        }
        // We add a new-line `\n` to correct an issue with counting line breaks
        // https://app.asana.com/0/1191520030670857/1206899715442131/f
        return `${serialize(p.children)}\n`;
      }
    })
    .join('');
};

export const getTextLength = (text: CustomParagraph[]): number => {
  // When we serialise, a new-line is added. For the count, we need to remove that once
  // serialisation is completed so we can have an accurate count.
  const serialized = serialize(text);
  // Only slice() the last character if we know that the ending is a new-line
  const editorText = serialized.endsWith('\n') ? serialized.slice(0, -1) : serialized;

  return countSymbols(editorText);
};
